/**
 * Clothing v2 — garments that fit any figure by construction.
 *
 * 1. Shells: a garment is a region of the body's own skin. A signed field F (min of constraints: neckline, hem, cuffs,
 *    holes) is evaluated on the welded body; triangles are clipped at F = 0 (clean straight hems/cuffs whatever the
 *    topology), the kept surface is pushed out along the smooth normals by the cloth thickness and relaxed (Laplacian)
 *    so fur bumps become fabric, while a minimum clearance keeps it outside the skin. Every shell vertex comes from a
 *    body vertex (or an edge between two), so it carries exactly the body's skin weights: the garment follows any
 *    morph and pose; covered body surfaces are omitted. A folded-under rim gives the hem a visible thickness.
 * 2. Drapes: loose parts (skirts, tabard panels, bell sleeves, capes) grow from a shell boundary: the boundary points are
 *    resampled by angle into ring 0 (overlapping the shell slightly), and rings descend along an axis, never narrower
 *    than the ring above nor than the body's measured envelope, flaring and folding toward the hem. Their weights blend
 *    from the boundary's weights to the pelvis (or the limb), so they swing as cloth instead of tearing between legs.
 */
import type { Skeleton, V3 } from './rig';
import { BIN, bindToBody, boneSet, catCollar, envelope, H_E1, H_E2, hat, sampleR, slice, torsoCentre, type Body, type ClothStyle, type Fit } from './garments';

export interface ClothPiece {
  id: string;
  positions: number[]; uv: number[]; joints: number[]; weights: number[]; indices: number[];
  /** Rest position (x, y, z) + distance to the free edge, for patterns and trims. */
  aux: number[];
  style: ClothStyle;
  /** Pattern frame centre (torso axis) and scale. */
  center: [number, number, number, number];
}

// ── welded body ───────────────────────────────────────────────────────────────────────────────────────────────────
export interface Welded {
  pos: Float32Array; nrm: Float32Array; idx: Uint32Array;
  owner: Uint8Array; joints: Uint8Array; weights: Float32Array;
  /** body vertex → welded id */
  map: Int32Array;
  nbr: number[][];
  /** Tiny disconnected facial details (whiskers, mouth lines) are not cloth surfaces. */
  clothable: Uint8Array;
}

export function weldBody(body: Body): Welded {
  const vc = body.pos.length / 3;
  const map = new Int32Array(vc);
  const ids = new Map<string, number>();
  const first: number[] = [];
  for (let i = 0; i < vc; i++) {
    const k = `${Math.round(body.pos[i * 3] * 2e4)},${Math.round(body.pos[i * 3 + 1] * 2e4)},${Math.round(body.pos[i * 3 + 2] * 2e4)}`;
    let g = ids.get(k);
    if (g === undefined) { g = first.length; ids.set(k, g); first.push(i); }
    map[i] = g;
  }
  const n = first.length;
  const pos = new Float32Array(n * 3), owner = new Uint8Array(n), joints = new Uint8Array(n * 4), weights = new Float32Array(n * 4);
  first.forEach((src, g) => {
    pos.set(body.pos.subarray(src * 3, src * 3 + 3), g * 3);
    owner[g] = body.owner[src];
    joints.set(body.joints.subarray(src * 4, src * 4 + 4), g * 4);
    weights.set(body.weights.subarray(src * 4, src * 4 + 4), g * 4);
  });
  const tris: number[] = [];
  for (let t = 0; t < body.indices.length; t += 3) {
    const a = map[body.indices[t]], b = map[body.indices[t + 1]], c = map[body.indices[t + 2]];
    if (a !== b && b !== c && a !== c) tris.push(a, b, c);
  }
  const idx = new Uint32Array(tris);
  const nrm = new Float32Array(n * 3);
  const nbrSet: Set<number>[] = Array.from({ length: n }, () => new Set<number>());
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t], b = idx[t + 1], c = idx[t + 2];
    const ux = pos[b * 3] - pos[a * 3], uy = pos[b * 3 + 1] - pos[a * 3 + 1], uz = pos[b * 3 + 2] - pos[a * 3 + 2];
    const vx = pos[c * 3] - pos[a * 3], vy = pos[c * 3 + 1] - pos[a * 3 + 1], vz = pos[c * 3 + 2] - pos[a * 3 + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const v of [a, b, c]) { nrm[v * 3] += nx; nrm[v * 3 + 1] += ny; nrm[v * 3 + 2] += nz; }
    nbrSet[a].add(b); nbrSet[a].add(c); nbrSet[b].add(a); nbrSet[b].add(c); nbrSet[c].add(a); nbrSet[c].add(b);
  }
  for (let i = 0; i < n; i++) { const l = Math.hypot(nrm[i * 3], nrm[i * 3 + 1], nrm[i * 3 + 2]) || 1; nrm[i * 3] /= l; nrm[i * 3 + 1] /= l; nrm[i * 3 + 2] /= l; }
  // smooth the normals once more so offsets do not follow every fur facet
  const sm = new Float32Array(nrm);
  for (let i = 0; i < n; i++) {
    let x = nrm[i * 3] * 2, y = nrm[i * 3 + 1] * 2, z = nrm[i * 3 + 2] * 2;
    for (const j of nbrSet[i]) { x += nrm[j * 3]; y += nrm[j * 3 + 1]; z += nrm[j * 3 + 2]; }
    const l = Math.hypot(x, y, z) || 1; sm[i * 3] = x / l; sm[i * 3 + 1] = y / l; sm[i * 3 + 2] = z / l;
  }
  const nbr = nbrSet.map((s) => [...s]), seen = new Uint8Array(n), clothable = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (!seen[i]) {
    const component = [i]; seen[i] = 1;
    for (let q = 0; q < component.length; q++) for (const j of nbr[component[q]]) if (!seen[j]) { seen[j] = 1; component.push(j); }
    if (component.length >= 100) for (const j of component) clothable[j] = 1;
  }
  return { pos, nrm: sm, idx, owner, joints, weights, map, nbr, clothable };
}

// ── constraints ───────────────────────────────────────────────────────────────────────────────────────────────────
/** A constraint is ≥ 0 inside the garment; its tag names the boundary it creates. */
export interface Constraint { tag: string; f: (p: V3, i: number) => number; excludedBones?: Set<number> }

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len = (a: V3) => Math.hypot(a[0], a[1], a[2]);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const norm = (v: V3): V3 => { const l = len(v) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };

/** Signed distance-like value along a limb: positive before `cut` (0 = joint start … 1 = end), for the limb's own vertices. */
function limbCut(w: Welded, bones: Set<number>, from: V3, to: V3, cut: number, tag: string): Constraint {
  const ax = sub(to, from), L = len(ax), u = norm(ax);
  return { tag, f: (p, i) => (bones.has(w.owner[i]) ? (cut - dot(sub(p, from), u) / L) * L : 1) };
}
/** Excludes the vertices of some bones (e.g. the tail, the ears): −1 on them. */
function exclude(w: Welded, bones: Set<number>, tag: string): Constraint {
  return { tag, f: (_p, i) => (bones.has(w.owner[i]) ? -0.02 : 1), excludedBones: bones };
}

// ── shell ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface ShellOpts {
  id: string;
  constraints: Constraint[];
  thickness: number;
  /** Extra offset as a function of rest position (looser over the belly, puffs, pleats). */
  loose?: (p: V3) => number;
  smooth?: number;
  rim?: boolean;
  /** Open cranial cuts need one edge, without a second folded strip above the skull. */
  rimTags?: string[];
  ceiling?: number;
  /** Joined seams are not free edges: do not paint a second hem under an attached drape. */
  trimTags?: string[];
  /** Export short cuff cuts so source triangles can end at the same seam. */
  clipCuffs?: boolean;
  style: ClothStyle;
  center: [number, number, number, number];
}
export interface ShellResult { piece: ClothPiece; boundary: { p: V3; tag: string; j: number[]; w: number[] }[]; covered: Uint8Array; cuffCut?: Float32Array }

export function shell(w: Welded, o: ShellOpts): ShellResult {
  const n = w.pos.length / 3;
  const P = (i: number): V3 => [w.pos[i * 3], w.pos[i * 3 + 1], w.pos[i * 3 + 2]];
  const C = o.constraints.length;
  const cv = new Float32Array(n * C);
  const F = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let m = Infinity;
    for (let c = 0; c < C; c++) { const v = o.constraints[c].f(P(i), i); cv[i * C + c] = v; m = Math.min(m, v); }
    F[i] = w.clothable[i] ? m : -1;
  }
  // output vertices: kept body vertices + edge points
  const outPos: number[] = [], outBase: number[] = [], outNrm: number[] = [], outJ: number[][] = [], outW: number[][] = [], outF: number[] = [], outBnd: boolean[] = [], outTag: string[] = [];
  const keep = new Map<number, number>();
  const edge = new Map<string, number>();
  const addVertex = (p: V3, nm: V3, j: number[], wt: number[], f: number, bnd: boolean, tag: string): number => {
    outBase.push(...p); outPos.push(...p); outNrm.push(...nm); outJ.push(j); outW.push(wt); outF.push(f); outBnd.push(bnd); outTag.push(tag);
    return outF.length - 1;
  };
  const vOf = (i: number): number => {
    let id = keep.get(i);
    if (id === undefined) {
      id = addVertex(P(i), [w.nrm[i * 3], w.nrm[i * 3 + 1], w.nrm[i * 3 + 2]], [...w.joints.subarray(i * 4, i * 4 + 4)], [...w.weights.subarray(i * 4, i * 4 + 4)], F[i], false, '');
      keep.set(i, id);
    }
    return id;
  };
  const eOf = (a: number, b: number): number => {
    const key = a < b ? `${a}_${b}` : `${b}_${a}`;
    let id = edge.get(key);
    if (id !== undefined) return id;
    const t = F[a] / (F[a] - F[b]);
    const p: V3 = [lerp(w.pos[a * 3], w.pos[b * 3], t), lerp(w.pos[a * 3 + 1], w.pos[b * 3 + 1], t), lerp(w.pos[a * 3 + 2], w.pos[b * 3 + 2], t)];
    const nm = norm([lerp(w.nrm[a * 3], w.nrm[b * 3], t), lerp(w.nrm[a * 3 + 1], w.nrm[b * 3 + 1], t), lerp(w.nrm[a * 3 + 2], w.nrm[b * 3 + 2], t)]);
    const wm = new Map<number, number>();
    for (let k = 0; k < 4; k++) {
      wm.set(w.joints[a * 4 + k], (wm.get(w.joints[a * 4 + k]) ?? 0) + w.weights[a * 4 + k] * (1 - t));
      wm.set(w.joints[b * 4 + k], (wm.get(w.joints[b * 4 + k]) ?? 0) + w.weights[b * 4 + k] * t);
    }
    const top = [...wm.entries()].sort((x, y) => y[1] - x[1]).slice(0, 4);
    const s = top.reduce((q, e) => q + e[1], 0) || 1;
    // which constraint made this boundary
    let best = 0, bv = Infinity;
    for (let c = 0; c < C; c++) { const v = lerp(cv[a * C + c], cv[b * C + c], t); if (v < bv) { bv = v; best = c; } }
    id = addVertex(p, nm, top.map((e) => e[0]).concat([0, 0, 0, 0]).slice(0, 4), top.map((e) => e[1] / s).concat([0, 0, 0, 0]).slice(0, 4), 0, true, o.constraints[best]?.tag ?? '');
    edge.set(key, id);
    return id;
  };
  const tris: number[] = [];
  const bseg: [number, number][] = [];
  for (let t = 0; t < w.idx.length; t += 3) {
    const v = [w.idx[t], w.idx[t + 1], w.idx[t + 2]];
    const ins = v.map((i) => F[i] >= 0);
    const k = ins.filter(Boolean).length;
    if (k === 0) continue;
    if (k === 3) { tris.push(vOf(v[0]), vOf(v[1]), vOf(v[2])); continue; }
    // rotate so the odd one out is first
    let r = 0;
    if (k === 1) r = ins.indexOf(true); else r = ins.indexOf(false);
    const a = v[r], b = v[(r + 1) % 3], c = v[(r + 2) % 3];
    if (k === 1) { // a inside
      const ab = eOf(a, b), ac = eOf(a, c);
      tris.push(vOf(a), ab, ac);
      bseg.push([ab, ac]);
    } else { // a outside, b c inside
      const ab = eOf(a, b), ac = eOf(a, c);
      tris.push(ab, vOf(b), vOf(c), ab, vOf(c), ac);
      bseg.push([ac, ab]);
    }
  }
  const m = outF.length;
  // adjacency on the output mesh
  const nb: Set<number>[] = Array.from({ length: m }, () => new Set<number>());
  for (let t = 0; t < tris.length; t += 3) { const a = tris[t], b = tris[t + 1], c = tris[t + 2]; nb[a].add(b); nb[a].add(c); nb[b].add(a); nb[b].add(c); nb[c].add(a); nb[c].add(b); }
  // offset, relax, keep clearance
  const thick = (i: number) => o.thickness + (o.loose ? o.loose([outBase[i * 3], outBase[i * 3 + 1], outBase[i * 3 + 2]]) : 0);
  for (let i = 0; i < m; i++) { const th = thick(i); for (let k = 0; k < 3; k++) outPos[i * 3 + k] = outBase[i * 3 + k] + outNrm[i * 3 + k] * th; }
  const iters = o.smooth ?? 4;
  for (let it = 0; it < iters; it++) {
    const next = outPos.slice();
    for (let i = 0; i < m; i++) {
      const ns = [...nb[i]];
      if (!ns.length) continue;
      // boundary vertices relax only along the boundary (straight hems), interior ones freely
      const pool = outBnd[i] ? ns.filter((j) => outBnd[j]) : ns;
      if (!pool.length) continue;
      for (let k = 0; k < 3; k++) {
        let s = 0; for (const j of pool) s += outPos[j * 3 + k];
        next[i * 3 + k] = outPos[i * 3 + k] + 0.55 * (s / pool.length - outPos[i * 3 + k]);
      }
    }
    for (let i = 0; i < m; i++) {
      const th = thick(i) * 0.9;
      const d = (next[i * 3] - outBase[i * 3]) * outNrm[i * 3] + (next[i * 3 + 1] - outBase[i * 3 + 1]) * outNrm[i * 3 + 1] + (next[i * 3 + 2] - outBase[i * 3 + 2]) * outNrm[i * 3 + 2];
      if (d < th) for (let k = 0; k < 3; k++) next[i * 3 + k] += outNrm[i * 3 + k] * (th - d);
    }
    for (let i = 0; i < next.length; i++) outPos[i] = next[i];
  }
  if (o.ceiling !== undefined) for (let i = 0; i < m; i++) outPos[i * 3 + 1] = Math.min(outPos[i * 3 + 1], o.ceiling);
  // folded-under rim along every boundary
  const rimTris: number[] = [];
  if (o.rim !== false) {
    const dup = new Map<number, number>();
    const under = (i: number): number => {
      let id = dup.get(i);
      if (id !== undefined) return id;
      const th = thick(i);
      const p: V3 = outTag[i].startsWith('cuff')
        ? [outBase[i * 3], outBase[i * 3 + 1], outBase[i * 3 + 2]]
        : [outPos[i * 3] - outNrm[i * 3] * th * 0.55, outPos[i * 3 + 1] - outNrm[i * 3 + 1] * th * 0.55, outPos[i * 3 + 2] - outNrm[i * 3 + 2] * th * 0.55];
      id = addVertex([outBase[i * 3], outBase[i * 3 + 1], outBase[i * 3 + 2]], [outNrm[i * 3], outNrm[i * 3 + 1], outNrm[i * 3 + 2]], outJ[i], outW[i], 0, true, outTag[i]);
      outPos[id * 3] = p[0]; outPos[id * 3 + 1] = p[1]; outPos[id * 3 + 2] = p[2];
      dup.set(i, id);
      return id;
    };
    for (const [a, b] of bseg) {
      if (o.rimTags && (!o.rimTags.includes(outTag[a]) || !o.rimTags.includes(outTag[b]))) continue;
      const ua = under(a), ub = under(b); rimTris.push(a, b, ub, a, ub, ua);
    }
  }
  // distance-to-edge for trims: geodesic-ish BFS over the mesh from the boundary
  const dist = new Float32Array(outF.length).fill(1e9);
  const queue: number[] = [];
  for (let i = 0; i < outF.length; i++) if (outBnd[i] && (!o.trimTags || o.trimTags.includes(outTag[i]))) { dist[i] = 0; queue.push(i); }
  for (let q = 0; q < queue.length; q++) {
    const i = queue[q];
    if (i >= m) continue;
    for (const j of nb[i]) {
      const d = dist[i] + Math.hypot(outBase[j * 3] - outBase[i * 3], outBase[j * 3 + 1] - outBase[i * 3 + 1], outBase[j * 3 + 2] - outBase[i * 3 + 2]);
      if (d < dist[j]) { dist[j] = d; queue.push(j); }
    }
  }
  const total = outF.length;
  // A clipped tail/ear hole must not leave residual tail/ear influences in the
  // surrounding cloth. Those tiny weights caused long spikes during motion.
  const excluded = new Set(o.constraints.flatMap(c => [...(c.excludedBones ?? [])]));
  for (let i = 0; i < total; i++) {
    const sum = outW[i].reduce((s, value, k) => s + (excluded.has(outJ[i][k]) ? 0 : value), 0);
    if (sum > 1e-8) outW[i] = outW[i].map((value, k) => excluded.has(outJ[i][k]) ? 0 : value / sum);
  }
  const aux: number[] = [], uv: number[] = [], joints: number[] = [], weights: number[] = [];
  for (let i = 0; i < total; i++) {
    aux.push(outBase[i * 3], outBase[i * 3 + 1], outBase[i * 3 + 2], Math.min(dist[i], 1));
    uv.push(0, 0);
    joints.push(...outJ[i]); weights.push(...outW[i]);
  }
  const covered = new Uint8Array(n);
  for (let i = 0; i < n; i++) covered[i] = F[i] > 0.004 ? 1 : 0;
  const cuffCut = o.clipCuffs ? new Float32Array(n).fill(NaN) : undefined;
  if (cuffCut) for (let i = 0; i < n; i++) {
    // Other openings, necks, detached face ribbons and tail holes keep their
    // existing coverage. Only an arm cuff has a continuous cut field here.
    if (!w.clothable[i] || o.constraints.some((c, k) => !c.tag.startsWith('cuff') && cv[i * C + k] <= 0.004)) continue;
    // Match eOf's complete field, including the other endpoint's neckline or
    // hem distance. Cuff-only values become 1 on chest-owned endpoints and cut
    // arm/chest straddlers at a different point, leaving holes beside sleeves.
    // A small hidden underlap keeps the relaxed rim closed during bending.
    cuffCut[i] = F[i] - 0.004;
    if (cuffCut[i] <= 0) covered[i] = 0;
  }
  const boundary = [] as ShellResult['boundary'];
  for (let i = 0; i < m; i++) if (outBnd[i]) boundary.push({ p: [outPos[i * 3], outPos[i * 3 + 1], outPos[i * 3 + 2]], tag: outTag[i], j: outJ[i], w: outW[i] });
  return {
    piece: { id: o.id, positions: outPos.slice(0, total * 3), uv, joints, weights, indices: tris.concat(rimTris), aux, style: o.style, center: o.center },
    boundary, covered, cuffCut,
  };
}

// ── drapes ────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface DrapeOpts {
  id: string;
  /** Boundary points to hang from (already filtered by tag). */
  from: ShellResult['boundary'];
  /** Axis centre at the top and the hanging direction. */
  center: V3; dir: V3;
  length: number;
  /** Angular range in the ring basis (radians), null = closed ring. */
  arc: [number, number] | null;
  segments: number; rings: number;
  flare: number; folds: number; foldDepth: number;
  /** Measure the body envelope at each ring (skirts must clear the legs). */
  bodyClear?: { body: Body; bones: Set<number>; clearance: number };
  /** Weight blend toward this bone at the hem (0..1). */
  anchor: { bone: number; amount: number };
  overlap: number;
  dagged?: number;
  /** Lift open cape corners into a softly curved hem. */
  hemLift?: number;
  /** Loose layers share the same spine deformation below the chest. */
  spine?: Body;
  /** Connect the original shell boundary to the loft instead of overlapping two unrelated rings. */
  sew?: boolean;
  style: ClothStyle;
  patternCenter: [number, number, number, number];
}

export function drape(o: DrapeOpts): ClothPiece | null {
  if (o.from.length < 3) return null;
  const dir = norm(o.dir);
  const e1 = norm(Math.abs(dir[1]) > 0.9 ? [1, 0, 0] : [dir[2], 0, -dir[0]].map((x) => x) as V3);
  let e2 = norm([dir[1] * e1[2] - dir[2] * e1[1], dir[2] * e1[0] - dir[0] * e1[2], dir[0] * e1[1] - dir[1] * e1[0]]);
  // for a vertical drape we want the same basis as the horizontal rings: e1 = +x, e2 = +z (θ = +π/2 is the front)
  const E1: V3 = Math.abs(dir[1]) > 0.9 ? H_E1 : e1;
  const E2: V3 = Math.abs(dir[1]) > 0.9 ? H_E2 : e2;
  e2 = E2;
  const cols = o.arc ? o.segments + 1 : o.segments;
  const angleAt = (j: number) => (o.arc ? lerp(o.arc[0], o.arc[1], j / o.segments) : -Math.PI + (j / o.segments) * 2 * Math.PI);
  // ring 0: boundary points binned by angle (radius + height + weights per bin)
  const r0 = new Float32Array(BIN).fill(-1), h0 = new Float32Array(BIN).fill(0), cnt = new Float32Array(BIN);
  const binW: Map<number, number>[] = Array.from({ length: BIN }, () => new Map());
  for (const b of o.from) {
    const d = sub(b.p, o.center);
    const x = dot(d, E1), z = dot(d, e2), along = dot(d, dir);
    const a = Math.atan2(z, x);
    const k = Math.floor(((a + Math.PI) / (2 * Math.PI)) * BIN) % BIN;
    r0[k] = Math.max(r0[k], Math.hypot(x, z));
    h0[k] += along; cnt[k]++;
    for (let q = 0; q < 4; q++) binW[k].set(b.j[q], (binW[k].get(b.j[q]) ?? 0) + b.w[q]);
  }
  const filled = (arr: Float32Array, has: (i: number) => boolean): Float32Array => {
    const out = new Float32Array(arr);
    for (let i = 0; i < BIN; i++) {
      if (has(i)) continue;
      let lo = 1, hi = 1;
      while (lo < BIN && !has((i - lo + BIN) % BIN)) lo++;
      while (hi < BIN && !has((i + hi) % BIN)) hi++;
      out[i] = lerp(arr[(i - lo + BIN) % BIN], arr[(i + hi) % BIN], lo / (lo + hi));
    }
    return out;
  };
  if (r0.every((x) => x < 0)) return null;
  for (let k = 0; k < BIN; k++) if (cnt[k]) h0[k] /= cnt[k];
  const R0 = filled(r0, (i) => r0[i] >= 0), H0 = filled(h0, (i) => cnt[i] > 0);
  const binWeights = (a: number): Map<number, number> => {
    let k = Math.floor(((a + Math.PI) / (2 * Math.PI)) * BIN) % BIN; k = (k + BIN) % BIN;
    for (let s = 0; s < BIN; s++) { const kk = (k + (s % 2 ? -1 : 1) * Math.ceil(s / 2) + BIN) % BIN; if (binW[kk].size) return binW[kk]; }
    return new Map([[o.anchor.bone, 1]]);
  };
  const positions: number[] = [], uv: number[] = [], joints: number[] = [], weights: number[] = [], aux: number[] = [];
  let prev: Float32Array | null = null;
  for (let r = 0; r <= o.rings; r++) {
    const t = r / o.rings;
    const radii = new Float32Array(cols);
    // envelope of the body at this ring (only for vertical drapes)
    let env: Float32Array | null = null;
    if (o.bodyClear) {
      const start = H0.reduce((sum, value) => sum + value, 0) / BIN + (o.sew ? 0.018 : -o.overlap * 0.6);
      const along = lerp(start, o.length, t);
      const c: V3 = [o.center[0] + dir[0] * along, o.center[1] + dir[1] * along, o.center[2] + dir[2] * along];
      const pts = slice(o.bodyClear.body, o.bodyClear.bones, c, dir.map((x) => -x) as V3, 0.5);
      env = envelope(pts, c, E1, e2, 0.01);
    }
    for (let j = 0; j < cols; j++) {
      const a = angleAt(j);
      let rr = sampleR(R0, a) + o.overlap * (t === 0 ? 1 : 0.6) + o.flare * t * t + o.flare * 0.35 * t;
      // folds: alternating radial ripples that deepen toward the hem
      rr += Math.cos(a * o.folds) * o.foldDepth * t;
      // A fold trough is fabric too. Enforce the clearance after adding the folds.
      if (env) rr = Math.max(rr, sampleR(env, a) + o.bodyClear!.clearance);
      if (prev) rr = Math.max(rr, prev[j] * 0.995);
      radii[j] = rr;
    }
    // Relax sharp measured peaks outwards only: this softens the fabric
    // silhouette while preserving every collision-clearance lower bound.
    if (o.bodyClear) for (let pass = 0; pass < 2; pass++) {
      const previous = radii.slice();
      for (let j = 0; j < cols; j++) {
        const a = o.arc ? Math.max(0, j - 1) : (j + cols - 1) % cols;
        const b = o.arc ? Math.min(cols - 1, j + 1) : (j + 1) % cols;
        radii[j] = Math.max(previous[j], (previous[a] + 2 * previous[j] + previous[b]) / 4);
      }
    }
    prev = radii;
    for (let j = 0; j < cols; j++) {
      const a = angleAt(j);
      const startH = sampleR(H0, a) + (o.sew ? 0.018 : -o.overlap * 0.6);
      let along = lerp(startH, o.length, t);
      if (o.hemLift) along -= o.hemLift * Math.cos(a) ** 2 * t ** 3;
      if (o.dagged && r === o.rings) along -= (j % 2 ? 0 : o.dagged);
      const rr = radii[j];
      const p: V3 = [
        o.center[0] + dir[0] * along + (Math.cos(a) * E1[0] + Math.sin(a) * e2[0]) * rr,
        o.center[1] + dir[1] * along + (Math.cos(a) * E1[1] + Math.sin(a) * e2[1]) * rr,
        o.center[2] + dir[2] * along + (Math.cos(a) * E1[2] + Math.sin(a) * e2[2]) * rr,
      ];
      positions.push(...p);
      uv.push((a + Math.PI) / (2 * Math.PI), t);
      // Keep the hem-distance field continuous through the first ring. A large
      // sentinel at a sewn edge makes subpixel interpolation draw false trim
      // flecks across the middle of otherwise closed fabric in the GPU preview.
      aux.push(p[0], p[1], p[2], Math.max(0, (1 - t) * (o.length - startH)) * 0.6);
      // weights: boundary weights at the top → blend with the anchor toward the hem
      const wm = new Map(binWeights(a));
      // A back cape hangs from the shoulders. Importing the nearest sleeve's
      // arm weight made adjacent angular bins jump apart during a guard pose.
      if (o.hemLift) { wm.clear(); wm.set(o.anchor.bone, 1); }
      const s0 = [...wm.values()].reduce((q, x) => q + x, 0) || 1;
      for (const [k, v] of wm) wm.set(k, (v / s0) * (1 - o.anchor.amount * t));
      wm.set(o.anchor.bone, (wm.get(o.anchor.bone) ?? 0) + o.anchor.amount * t);
      if (o.spine && p[1] < o.spine.lm.chest[1]) {
        const { lm, sk } = o.spine;
        const chestAmount = Math.max(0, Math.min(1, (p[1] - lm.pelvis[1]) / (lm.chest[1] - lm.pelvis[1])));
        const blend = Math.min(1, Math.max(0, (o.center[1] - p[1]) / 0.07));
        for (const [bone, weight] of wm) wm.set(bone, weight * (1 - blend));
        const pelvis = sk.byName.get('pelvis')!, chest = sk.byName.get('chest')!;
        wm.set(pelvis, (wm.get(pelvis) ?? 0) + (1 - chestAmount) * blend);
        wm.set(chest, (wm.get(chest) ?? 0) + chestAmount * blend);
      }
      const top = [...wm.entries()].sort((x, y) => y[1] - x[1]).slice(0, 4);
      const s = top.reduce((q, e) => q + e[1], 0) || 1;
      for (let q = 0; q < 4; q++) { joints.push(top[q]?.[0] ?? 0); weights.push(top[q] ? top[q][1] / s : 0); }
    }
  }
  if (o.hemLift) {
    // A cape clears the haunches gradually; copying every horizontal envelope
    // literally formed a hard shelf above the tail. Relax only outwards so the
    // long drape stays outside both the body and the gown beneath it.
    for (let pass = 0; pass < 12; pass++) {
      const before = positions.slice();
      for (let r = 1; r < o.rings; r++) for (let j = 0; j < cols; j++) {
        const v = r * cols + j, a = angleAt(j);
        const radial: V3 = [Math.cos(a) * E1[0] + Math.sin(a) * e2[0], Math.cos(a) * E1[1] + Math.sin(a) * e2[1], Math.cos(a) * E1[2] + Math.sin(a) * e2[2]];
        const radius = (index: number) => dot(sub(before.slice(index * 3, index * 3 + 3) as V3, o.center), radial);
        const current = radius(v), softened = (radius(v - cols) + 2 * current + radius(v + cols)) / 4;
        const delta = Math.max(0, softened - current);
        for (let k = 0; k < 3; k++) positions[v * 3 + k] += radial[k] * delta;
      }
    }
    for (let v = 0; v < positions.length / 3; v++) for (let k = 0; k < 3; k++) aux[v * 4 + k] = positions[v * 3 + k];
  }
  const indices: number[] = [];
  for (let r = 0; r < o.rings; r++) for (let j = 0; j < o.segments; j++) {
    const j1 = o.arc ? j + 1 : (j + 1) % o.segments;
    const a = r * cols + j, b = r * cols + j1, c = (r + 1) * cols + j, d = (r + 1) * cols + j1;
    indices.push(a, b, c, b, d, c);
  }
  if (o.sew && !o.arc) {
    // Zipper triangulation joins two differently sampled closed rings. The
    // upper ring copies position AND weights exactly, so no pose can pull a
    // waist seam apart from its source tunic.
    const boundary = o.from.map(b => ({ ...b, angle: Math.atan2(dot(sub(b.p, o.center), e2), dot(sub(b.p, o.center), E1)) })).sort((a, b) => a.angle - b.angle);
    const start = positions.length / 3, n = boundary.length;
    // Near-vertical sleeves use horizontal rings, so their ring plane is not
    // exactly perpendicular to dir. Measure along that same plane at the seam.
    const plane: V3 = [E1[1] * e2[2] - E1[2] * e2[1], E1[2] * e2[0] - E1[0] * e2[2], E1[0] * e2[1] - E1[1] * e2[0]];
    const alongPlane = (p: V3) => dot(sub(p, o.center), plane) / dot(dir, plane);
    for (const b of boundary) {
      positions.push(...b.p); uv.push((b.angle + Math.PI) / (Math.PI * 2), 0);
      aux.push(...b.p, Math.max(0, o.length - alongPlane(b.p)) * 0.6);
      joints.push(...b.j); weights.push(...b.w);
    }
    const raw = (i: number) => start + (i + n - 1) % n;
    const rawAngle = (i: number) => i === 0 ? boundary[n - 1].angle - Math.PI * 2 : boundary[(i - 1) % n].angle + (i > n ? Math.PI * 2 : 0);
    let i = 0, j = 0;
    while (i < n || j < cols) {
      const nextRaw = i < n ? rawAngle(i + 1) : Infinity;
      const nextLoft = j < cols ? -Math.PI + (j + 1) / cols * Math.PI * 2 : Infinity;
      if (nextRaw < nextLoft) { indices.push(raw(i), raw(i + 1), j % cols); i++; }
      else { indices.push(raw(i), (j + 1) % cols, j % cols); j++; }
    }
  }
  return { id: o.id, positions, uv, joints, weights, indices, aux, style: o.style, center: o.patternCenter };
}

// ── outfit builders ───────────────────────────────────────────────────────────────────────────────────────────────
const S = (base: string, trim: string, accent: string, pattern: number, trimWidth = 0.014, folds = 0): ClothStyle => ({ base, trim, accent, pattern, trimWidth, folds });

interface Ctx { body: Body; w: Welded; sk: Skeleton; center: [number, number, number, number]; layers: ClothPiece[]; covered: Uint8Array }

/** Measure outer garments against the dressed figure, not just the naked cat. */
function dressedBody(c: Ctx, include: (piece: ClothPiece) => boolean = () => true): Body {
  const pos = [...c.body.pos], indices = [...c.body.indices], owner = [...c.body.owner];
  const joints = [...c.body.joints], weights = [...c.body.weights];
  for (const piece of c.layers.filter(include)) {
    const offset = pos.length / 3;
    pos.push(...piece.positions); joints.push(...piece.joints); weights.push(...piece.weights);
    for (let v = 0; v < piece.positions.length / 3; v++) {
      let strongest = 0;
      for (let k = 1; k < 4; k++) if (piece.weights[v * 4 + k] > piece.weights[v * 4 + strongest]) strongest = k;
      owner.push(piece.joints[v * 4 + strongest]);
    }
    for (const index of piece.indices) indices.push(offset + index);
  }
  return { ...c.body, pos: new Float32Array(pos), indices: new Uint32Array(indices), owner: new Uint8Array(owner), joints: new Uint8Array(joints), weights: new Float32Array(weights) };
}

function bones(sk: Skeleton, names: string[]): Set<number> { return boneSet(sk, names); }
function chainNames(sk: Skeleton, prefix: string): string[] { return sk.bones.filter((b) => b.name.startsWith(prefix) || b.chain === prefix).map(b => b.name); }

/** Torso garment shell (neckline → hem) with optional sleeves (cuff 0..1 along the arm) and leg coverage. */
function torsoShell(c: Ctx, o: { id: string; neckDrop: number; vNeck: number; hemY: number; sleeve: number; thickness: number; loose: number; neckTrim?: boolean; style: ClothStyle }): ShellResult {
  const { body, w, sk } = c, lm = body.lm;
  const neckY = lerp(lm.chest[1], lm.neck[1], 0.9) - o.neckDrop;
  const arms = bones(sk, ['upperArmR', 'upperArmL', 'foreArmR', 'foreArmL', 'handR', 'handL']);
  const cons: Constraint[] = [
    { tag: 'neck', f: (p) => neckY - o.vNeck * Math.max(0, Math.min(1, (p[2] - lm.neck[2]) / 0.04)) * Math.exp(-((p[0] - lm.neck[0]) ** 2) / 0.0012) - p[1] },
    // Long relaxed arms can pass below the waist. They end at their own cuff,
    // not at the torso hem: otherwise the skirt sews itself to a wrist loop
    // and stretches into a web when the character raises that arm.
    { tag: 'hem', f: (p, i) => arms.has(w.owner[i]) ? 1 : p[1] - o.hemY },
    exclude(w, bones(sk, [...chainNames(sk, 'tail'), 'head', ...chainNames(sk, 'ear')]), 'hole'),
  ];
  for (const s of ['R', 'L'] as const) {
    const sh = s === 'R' ? lm.shoulderR : lm.shoulderL, wr = s === 'R' ? lm.wristR : lm.wristL;
    const arm = bones(sk, [`upperArm${s}`, `foreArm${s}`, `hand${s}`]);
    cons.push(limbCut(w, arm, sh, wr, o.sleeve, `cuff${s}`));
  }
  const belly = c.center;
  return shell(w, {
    id: o.id, constraints: cons, thickness: o.thickness, smooth: 5, style: o.style, center: c.center, trimTags: [...(o.neckTrim === false ? [] : ['neck']), 'cuffR', 'cuffL'],
    clipCuffs: o.sleeve > 0.2 && o.sleeve < 1,
    loose: (p) => o.loose * Math.max(0, 1 - Math.hypot(p[0] - belly[0], (p[1] - lm.pelvis[1]) * 0.8) / 0.25) * Math.max(0, (p[2] - lm.pelvis[2]) * 4 + 0.3),
  });
}

function skirt(c: Ctx, sh: ShellResult, o: { id: string; length: number; flare: number; folds: number; foldDepth: number; arc: [number, number] | null; dagged?: number; style: ClothStyle }): ClothPiece | null {
  const { body, sk } = c, lm = body.lm;
  const boundary = sh.boundary.filter((b) => b.tag === 'hem');
  const top = boundary.reduce((a, b) => a + b.p[1], 0) / Math.max(1, boundary.length);
  const from = boundary;
  const center: V3 = [lm.pelvis[0], top, lm.pelvis[2]];
  const legs = bones(sk, ['pelvis', 'thighR', 'thighL', 'shinR', 'shinL', 'footR', 'footL']);
  const underlayer = (piece: ClothPiece) => piece.id === 'hose' || piece.id === 'chausses';
  const overTrousers = c.layers.some(underlayer);
  // A closed skirt replaces the covered legs. Otherwise the original body
  // triangles can straddle the shell/drape seam and flash through the fabric.
  if (o.arc === null) for (let i = 0; i < c.covered.length; i++) {
    const y = c.w.pos[i * 3 + 1];
    if (legs.has(c.w.owner[i]) && y < top + 0.016 && y > o.length + 0.025) c.covered[i] = 1;
  }
  return drape({
    id: o.id, from, center, dir: [0, -1, 0], length: Math.max(0.04, top - o.length), arc: o.arc, segments: 64, rings: 10,
    // The drape must clear the trousers/chausses, not only the bare thigh.
    // Their outward shell otherwise consumes the gap on a slim torso with
    // longer legs. A further 0.004H of ease accommodates the different pelvis
    // and thigh scaling as a clothed knee bends; robes over bare legs retain
    // their original clearance and silhouette.
    flare: o.flare, folds: o.folds, foldDepth: o.foldDepth, bodyClear: { body: overTrousers ? dressedBody(c, underlayer) : body, bones: legs, clearance: overTrousers ? 0.016 : 0.012 },
    anchor: { bone: sk.byName.get('pelvis')!, amount: 0.55 }, overlap: 0.012, dagged: o.dagged, spine: body, sew: o.arc === null, style: o.style, patternCenter: c.center,
  });
}

function bellSleeve(c: Ctx, sh: ShellResult, side: 'R' | 'L', o: { id: string; length: number; flare: number; style: ClothStyle }): ClothPiece | null {
  const lm = c.body.lm;
  const from = sh.boundary.filter((b) => b.tag === `cuff${side}`);
  if (from.length < 3) return null;
  const el = side === 'R' ? lm.elbowR : lm.elbowL, wr = side === 'R' ? lm.wristR : lm.wristL;
  const dir = norm(sub(wr, el));
  const cx: V3 = [from.reduce((a, b) => a + b.p[0], 0) / from.length, from.reduce((a, b) => a + b.p[1], 0) / from.length, from.reduce((a, b) => a + b.p[2], 0) / from.length];
  return drape({
    id: o.id, from, center: cx, dir, length: o.length, arc: null, segments: 32, rings: 7, flare: o.flare, folds: 5, foldDepth: 0.002,
    anchor: { bone: c.sk.byName.get(`foreArm${side}`)!, amount: 0.2 }, overlap: 0.004, sew: true, style: o.style, patternCenter: c.center,
  });
}

/** Shoulder mantle shell + a back cape hanging from its hem. */
function cape(c: Ctx, o: { id: string; length: number; style: ClothStyle; mantle?: ClothStyle; backOnly?: boolean }): ClothPiece[] {
  const { body, w, sk } = c, lm = body.lm;
  const neckY = lerp(lm.chest[1], lm.neck[1], 0.92);
  const hemY = lerp(lm.chest[1], lm.neck[1], 0.35);
  const sh = shell(w, {
    id: `${o.id}-mantle`, thickness: 0.031, smooth: 6, style: o.mantle ?? o.style, center: c.center, trimTags: ['neck', 'front'],
    constraints: [
      { tag: 'neck', f: (p) => neckY - p[1] },
      { tag: 'hem', f: (p) => p[1] - hemY },
      exclude(w, bones(sk, ['head', ...chainNames(sk, 'ear'), ...chainNames(sk, 'tail'), 'foreArmR', 'foreArmL', 'handR', 'handL']), 'hole'),
      // a travelling cape hangs off the back and the shoulder tops only, leaving the chest (and its heraldry) free
      ...(o.backOnly ? [{ tag: 'front', f: (p: V3) => lm.chest[2] + 0.015 - p[2] + Math.max(0, p[1] - lerp(lm.chest[1], lm.neck[1], 0.75)) * 0.6 }] : []),
    ],
  });
  const from = sh.boundary.filter((b) => b.tag === 'hem' && b.p[2] < lm.chest[2] + 0.03);
  const top = from.reduce((a, b) => a + b.p[1], 0) / Math.max(1, from.length);
  const torso = bones(sk, ['pelvis', 'chest', 'thighR', 'thighL', 'shinR', 'shinL', 'upperArmR', 'upperArmL']);
  const back = drape({
    id: o.id, from, center: [lm.chest[0], top, lm.chest[2]], dir: [0, -1, 0], length: Math.max(0.05, top - o.length), arc: [-Math.PI + 0.12, -0.12],
    segments: 64, rings: 16, flare: 0.05, folds: 9, foldDepth: 0.006, bodyClear: { body: dressedBody(c), bones: torso, clearance: 0.028 },
    anchor: { bone: sk.byName.get('chest')!, amount: 0.5 }, overlap: 0.008, hemLift: 0.035, spine: body, style: o.style, patternCenter: c.center,
  });
  return [sh.piece, ...(back ? [back] : [])];
}

function hood(c: Ctx, o: { id: string; style: ClothStyle; liripipe: number }): ClothPiece[] {
  const { body, w, sk } = c, lm = body.lm;
  const fr = lm.face;
  // A long slender neck gets a coif ending at the jaw instead of a stretched
  // skin-tight cloth tube running all the way down to the shoulder collar.
  const slenderNeck = lm.head[1] - lm.neck[1] > lm.radius.head * 0.95;
  const neckY = slenderNeck ? lm.head[1] - lm.radius.head * 0.85 : lm.neck[1] - 0.02;
  const mappedRoots = [lm.earRPath?.[0], lm.earLPath?.[0]].filter((p): p is V3 => !!p);
  const earRoots = mappedRoots.length ? mappedRoots.map(p => p[1]) : sk.bones.filter(b => b.chain === 'ear' && sk.bones[b.parent]?.chain === 'head').map(b => b.head[1]);
  const hoodTop = Math.min(lm.headTop[1], earRoots.length ? Math.max(...earRoots) + 0.02 : lm.headTop[1]);
  const cons: Constraint[] = [
    { tag: 'neck', f: (p) => p[1] - neckY },
    // Skull ownership can include the broad outer edge of a long ear. Clip at
    // the cranial landmark as well, so rabbit ears never acquire detached sleeves.
    { tag: 'crown', f: (p) => hoodTop - p[1] },
    // Whiskers are part of the GLB head mesh. They must never become tiny cloth
    // fingers around the muzzle when the body surface is copied into a hood.
    // Cut the opening in skull space. A face-space ellipse reaches through
    // a thin skull and used to erase Inky's entire hood behind the face too.
    { tag: 'face', f: (p) => lm.head[2] + lm.radius.head * 0.15 - p[2] },
    { tag: 'face', f: (p) => {
      if (!fr || p[2] < lm.head[2] - lm.radius.head * 0.1) return 1;
      const u = (p[0] - fr.center[0]) / fr.scale, v = (p[1] - fr.center[1]) / fr.scale;
      return (Math.hypot(u / 1.6, (v + 0.45) / 1.9) - 1) * fr.scale * 0.7;
    } },
    exclude(w, bones(sk, [...chainNames(sk, 'tail'), ...chainNames(sk, 'ear'), 'upperArmR', 'upperArmL', 'foreArmR', 'foreArmL', 'handR', 'handL', 'pelvis']), 'hole'),
  ];
  const style = { ...o.style, trimWidth: Math.min(o.style.trimWidth, 0.006) };
  const sh = shell(w, { id: o.id, constraints: cons, thickness: 0.014, smooth: 8, style, center: c.center, trimTags: ['face'], rimTags: ['face', 'neck'], ceiling: hoodTop });
  for (let i = 0; i < c.covered.length; i++) c.covered[i] |= sh.covered[i];
  const collar = band(c, { id: `${o.id}-collar`, y: lerp(lm.chest[1], lm.neck[1], 0.8), half: (lm.neck[1] - lm.chest[1]) * 0.08, thickness: 0.025, pleats: 0, puff: 0, frontDrop: 0.018, style: { ...o.style, trimWidth: 0.002 }, bones: ['chest', 'neck'] });
  const out: ClothPiece[] = [sh.piece, collar.piece];
  // liripipe: a tapering tube from the top-back of the hood
  if (o.liripipe > 0) {
    const base: V3 = [lm.head[0], hoodTop - 0.025, lm.head[2] - 0.05];
    const tip: V3 = [lm.head[0], hoodTop - 0.055 * o.liripipe, lm.head[2] - 0.15 * o.liripipe];
    const rings = 5, seg = 10;
    const positions: number[] = [], uv: number[] = [], joints: number[] = [], weights: number[] = [], aux: number[] = [], indices: number[] = [];
    const head = sk.byName.get('head')!;
    for (let r = 0; r <= rings; r++) {
      const t = r / rings;
      const cc: V3 = [lerp(base[0], tip[0], t), lerp(base[1], tip[1], t) - Math.sin(t * Math.PI) * 0.03 * o.liripipe, lerp(base[2], tip[2], t)];
      const rad = lerp(0.033, 0.004, t);
      for (let j = 0; j < seg; j++) {
        const a = (j / seg) * Math.PI * 2;
        const p: V3 = [cc[0] + Math.cos(a) * rad, cc[1] + Math.sin(a) * rad, cc[2]];
        positions.push(...p); uv.push(j / seg, t); aux.push(...p, 1); joints.push(head, 0, 0, 0); weights.push(1, 0, 0, 0);
      }
    }
    for (let r = 0; r < rings; r++) for (let j = 0; j < seg; j++) {
      const j1 = (j + 1) % seg, a = r * seg + j, b = r * seg + j1, cc = (r + 1) * seg + j, d = (r + 1) * seg + j1;
      indices.push(a, cc, b, b, cc, d);
    }
    out.push({ id: `${o.id}-liripipe`, positions, uv, joints, weights, indices, aux, style, center: c.center });
  }
  return out;
}

function band(c: Ctx, o: { id: string; y: number; half: number; thickness: number; pleats: number; puff: number; frontDrop?: number; style: ClothStyle; bones: string[] }): ShellResult {
  const { body, w, sk } = c;
  const allowed = bones(sk, o.bones);
  const dressed = dressedBody(c, (piece) => !/sleeve|mantle|cope|cape|hood|cowl|skirt|hose|chausses|tabard-front|tabard-back/.test(piece.id));
  const positions: number[] = [], uv: number[] = [], indices: number[] = [], aux: number[] = [];
  const neckBand = o.bones.includes('neck');
  const segments = 64, rings = neckBand ? 6 : 4;
  // A belt is a closed woven strip; clipping a narrow band out of the cat's topology
  // left disconnected side loops when a loose tunic sat in front of it.
  for (let r = 0; r <= rings; r++) {
    const t = r / rings, y = lerp(o.y + o.half, o.y - o.half, t);
    const axis = torsoCentre(body.lm, y);
    // A narrow waist needs denser angle samples than a loose cape. Otherwise a
    // side peak spreads into the front bins and leaves the belt floating there.
    const radii = envelope(slice(dressed, allowed, axis, [0, 1, 0], 0.5), axis, H_E1, H_E2, 0.03, neckBand ? BIN : 64);
    for (let j = 0; j < segments; j++) {
      const a = -Math.PI + j / segments * Math.PI * 2;
      const pleat = o.puff * (0.5 + 0.5 * Math.cos(a * o.pleats));
      const radius = sampleR(radii, a) + 0.005 + Math.sin(t * Math.PI) * (neckBand ? 0.007 : 0.002) + pleat;
      const p: V3 = [axis[0] + Math.cos(a) * radius, y - (o.frontDrop ?? 0) * Math.max(0, Math.sin(a)) ** 2, axis[2] + Math.sin(a) * radius];
      positions.push(...p); uv.push(j / segments, t); aux.push(...p, Math.min(t, 1 - t) * o.half * 2);
    }
  }
  for (let r = 0; r < rings; r++) for (let j = 0; j < segments; j++) {
    const a = r * segments + j, b = r * segments + (j + 1) % segments, d = b + segments, e = a + segments;
    indices.push(a, b, e, b, d, e);
  }
  const skin = bindToBody(dressed, positions, allowed);
  for (let v = 0; v < positions.length / 3; v++) {
    let total = 0;
    for (let k = 0; k < 4; k++) { if (!allowed.has(skin.joints[v * 4 + k])) skin.weights[v * 4 + k] = 0; total += skin.weights[v * 4 + k]; }
    if (total > 0) for (let k = 0; k < 4; k++) skin.weights[v * 4 + k] /= total;
  }
  return { piece: { id: o.id, positions, uv, indices, aux, ...skin, style: o.style, center: c.center }, boundary: [], covered: new Uint8Array(w.pos.length / 3) };
}

function legShell(c: Ctx, o: { id: string; fromY: number; toY: number; thickness: number; style: ClothStyle }): ShellResult {
  const { w, sk } = c;
  const legs = bones(sk, ['thighR', 'thighL', 'shinR', 'shinL', 'footR', 'footL', 'pelvis']);
  return shell(w, {
    id: o.id, thickness: o.thickness, smooth: 3, style: o.style, center: c.center,
    constraints: [
      { tag: 'top', f: (p) => o.fromY - p[1] },
      { tag: 'bottom', f: (p) => p[1] - o.toY },
      { tag: 'hole', f: (_p, i) => (legs.has(w.owner[i]) ? 1 : -0.02) },
    ],
  });
}

/** A small raised brass frame follows the middle of the fitted waist belt. */
function buckle(belt: ClothPiece): ClothPiece {
  const vertex = 2 * 64 + 48, at = belt.positions.slice(vertex * 3, vertex * 3 + 3);
  const width = 0.038, height = Math.abs(belt.positions[1] - belt.positions[4 * 64 * 3 + 1]) * 0.8, stroke = 0.005;
  const positions: number[] = [], uv: number[] = [], joints: number[] = [], weights: number[] = [], indices: number[] = [], aux: number[] = [];
  const box = (x: number, y: number, w: number, h: number) => {
    const start = positions.length / 3;
    for (const [dx, dy, dz] of [[-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],[-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1]]) {
      const p = [at[0] + x + dx * w / 2, at[1] + y + dy * h / 2, at[2] + 0.006 + dz * 0.003];
      positions.push(...p); uv.push((dx + 1) / 2, (dy + 1) / 2); aux.push(...p, 0.01);
      joints.push(...belt.joints.slice(vertex * 4, vertex * 4 + 4)); weights.push(...belt.weights.slice(vertex * 4, vertex * 4 + 4));
    }
    for (const i of [0,2,1,0,3,2,4,5,6,4,6,7,0,1,5,0,5,4,3,7,6,3,6,2,0,4,7,0,7,3,1,2,6,1,6,5]) indices.push(start + i);
  };
  box(-width / 2, 0, stroke, height + stroke); box(width / 2, 0, stroke, height + stroke);
  box(0, -height / 2, width, stroke); box(0, height / 2, width, stroke);
  return { id: `${belt.id}-buckle`, positions, uv, joints, weights, indices, aux, center: belt.center, style: S('#c49a45', '#ecd088', '#6e4822', 4, 0.002) };
}

/** Mail is cut as separate sleeves and leggings, so its links follow each
 * anatomical chain instead of circling the torso through every limb.
 * Only pattern coordinates change: positions, topology and skin remain exact.
 */
function mailCoordinates(piece: ClothPiece, body: Body): void {
  const lm = body.lm;
  const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const frames = new Map<string, { origin: V3; axis: V3; across: V3; front: V3; links: number }>();
  const addFrame = (name: string, origin: V3, end: V3, radius: number): void => {
    const axis = norm(sub(end, origin)), across = norm(cross(axis, [0, 0, 1])), front = norm(cross(across, axis));
    frames.set(name, { origin, axis, across, front, links: Math.max(8, Math.round(Math.PI * 2 * (radius + 0.011) / 0.0145)) });
  };
  addFrame('spine', lm.neck, lm.pelvis, lm.radius.torso);
  for (const side of ['R', 'L'] as const) {
    addFrame(`arm${side}`, lm[`shoulder${side}`], lm[`wrist${side}`], lm.radius.arm);
    addFrame(`leg${side}`, lm[`hip${side}`], lm[`ankle${side}`], lm.radius.leg);
  }
  const chains: string[] = [], radii = new Map<string, number[]>();
  for (let v = 0; v < piece.positions.length / 3; v++) {
    const chainWeight = new Map<string, number>();
    for (let k = 0; k < 4; k++) {
      const chain = body.sk.bones[piece.joints[v * 4 + k]].chain;
      if (frames.has(chain)) chainWeight.set(chain, (chainWeight.get(chain) ?? 0) + piece.weights[v * 4 + k]);
    }
    // Upper leg cloth may still carry pelvis weights. Its material stays on
    // the same leg chart, avoiding a faded triangle at that skinning blend.
    const chain = piece.id === 'chausses' ? (piece.positions[v * 3] < lm.pelvis[0] ? 'legR' : 'legL')
      : [...chainWeight].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'spine';
    const frame = frames.get(chain)!, p = sub(piece.positions.slice(v * 3, v * 3 + 3) as V3, frame.origin);
    piece.aux[v * 4] = dot(p, frame.across);
    piece.aux[v * 4 + 1] = dot(p, frame.axis);
    piece.aux[v * 4 + 2] = dot(p, frame.front);
    chains.push(chain);
    const samples = radii.get(chain) ?? [];
    samples.push(Math.hypot(piece.aux[v * 4], piece.aux[v * 4 + 2])); radii.set(chain, samples);
  }
  // Authored rig radii are capture envelopes, often much wider than a thin
  // shin. Measure the actual cloth so its links keep the same physical size.
  for (const [chain] of radii) {
    const group = chain.replace(/[RL]$/, ''), samples = [...radii].filter(([name]) => name.replace(/[RL]$/, '') === group).flatMap(([, values]) => values);
    samples.sort((a, b) => a - b);
    frames.get(chain)!.links = Math.max(8, Math.round(Math.PI * 2 * samples[Math.floor(samples.length * 0.5)] / 0.0145));
  }
  for (let v = 0; v < chains.length; v++) piece.uv[v * 2] = frames.get(chains[v])!.links;
}

export interface OutfitV2 { pieces: ClothPiece[]; /** per body vertex: hidden under cloth */ covered: Uint8Array; /** Signed short cuff field, NaN elsewhere. */ cuffCut?: Float32Array }

/** Rigid pieces of the old system (hats, the collar bell) in the v2 format. */
function fromFit(f: Fit, center: [number, number, number, number]): ClothPiece {
  const p = f.piece;
  const aux: number[] = [];
  for (let i = 0; i < p.positions.length / 3; i++) aux.push(p.positions[i * 3], p.positions[i * 3 + 1], p.positions[i * 3 + 2], Math.min(p.uv[i * 2 + 1], 1 - p.uv[i * 2 + 1]) * 0.08);
  return { id: p.id, positions: p.positions, uv: p.uv, joints: p.joints, weights: p.weights, indices: p.indices, aux, style: { ...p.style, trimWidth: p.style.trimWidth * 0.06 }, center };
}

export const OUTFITS_V2 = [
  { id: 'none', name: 'Нагой' },
  { id: 'crusader', name: 'Крестоносец' },
  { id: 'monk', name: 'Монах' },
  { id: 'king', name: 'Король' },
  { id: 'bishop', name: 'Епископ' },
  { id: 'wanderer', name: 'Бродяга' },
  { id: 'jester', name: 'Шут' },
];

export function buildOutfitV2(body: Body, id: string, collar = false): OutfitV2 {
  const w = weldBody(body);
  const sk = body.sk, lm = body.lm;
  const center: [number, number, number, number] = [lm.chest[0], lm.chest[1], lm.chest[2], 1];
  const pieces: ClothPiece[] = [];
  const covered = new Uint8Array(w.pos.length / 3);
  let cuffCut: Float32Array | undefined;
  const c: Ctx = { body, w, sk, center, layers: pieces, covered };
  const cover = (r: ShellResult) => { for (let i = 0; i < covered.length; i++) covered[i] |= r.covered[i]; if (r.cuffCut) cuffCut = r.cuffCut; pieces.push(r.piece); };
  const hip = lm.hipR[1], knee = lm.kneeR[1], ankle = lm.ankleR[1];
  const waist = lerp(lm.pelvis[1], lm.chest[1], 0.3);
  // Cut the torso at the waist, above the crotch split. A hip-height cut can
  // produce two separate leg loops; treating them as one skirt ring created
  // a triangular hole and pinched fabric in the centre of every long robe.
  const hemAbove = Math.max(hip + 0.025, waist - 0.02);
  switch (id) {
    case 'crusader': {
      cover(torsoShell(c, { id: 'mail', neckDrop: 0, vNeck: 0, hemY: hemAbove, sleeve: 0.92, thickness: 0.009, loose: 0, style: S('#8f969e', '#5b6168', '#c9cfd6', 5, 0.012) }));
      cover(legShell(c, { id: 'chausses', fromY: hip + 0.03, toY: ankle + 0.02, thickness: 0.008, style: S('#8f969e', '#5b6168', '#c9cfd6', 5, 0.01) }));
      const tab = torsoShell(c, { id: 'tabard', neckDrop: 0.01, vNeck: 0, hemY: hemAbove, sleeve: 0.08, thickness: 0.017, loose: 0.006, style: S('#efe6d2', '#9c7a22', '#b3261e', 1, 0.012) });
      cover(tab);
      const front = skirt(c, tab, { id: 'tabard-front', length: lerp(hip, knee, 0.75), flare: 0.012, folds: 0, foldDepth: 0, arc: [Math.PI / 2 - 0.85, Math.PI / 2 + 0.85], style: S('#efe6d2', '#9c7a22', '#b3261e', 1, 0.012) });
      const back = skirt(c, tab, { id: 'tabard-back', length: lerp(hip, knee, 0.75), flare: 0.012, folds: 0, foldDepth: 0, arc: [-Math.PI / 2 - 0.85, -Math.PI / 2 + 0.85], style: S('#efe6d2', '#9c7a22', '#b3261e', 0, 0.012) });
      if (front) pieces.push(front); if (back) pieces.push(back);
      cover(band(c, { id: 'belt', y: waist, half: 0.018, thickness: 0.03, pleats: 0, puff: 0, style: S('#4a2c17', '#c9a13b', '#c9a13b', 0, 0.004), bones: ['pelvis', 'chest'] }));
      pieces.push(...cape(c, { id: 'cape', length: lerp(knee, ankle, 0.3), backOnly: true, style: S('#9e2a1c', '#c9a13b', '#e9dcc0', 0, 0.012) }));
      break;
    }
    case 'monk': {
      const robe = torsoShell(c, { id: 'robe', neckDrop: 0.005, vNeck: 0, hemY: hemAbove, sleeve: 0.62, thickness: 0.015, loose: 0.012, neckTrim: false, style: S('#6b4a2e', '#4a3220', '#c9a13b', 0, 0.008) });
      cover(robe);
      const sk1 = skirt(c, robe, { id: 'robe-skirt', length: ankle - 0.005, flare: 0.05, folds: 11, foldDepth: 0.007, arc: null, style: S('#6b4a2e', '#4a3220', '#c9a13b', 0, 0.012) });
      if (sk1) pieces.push(sk1);
      for (const s of ['R', 'L'] as const) { const b = bellSleeve(c, robe, s, { id: `sleeve${s}`, length: 0.07, flare: 0.008, style: S('#6b4a2e', '#4a3220', '#c9a13b', 0, 0.006) }); if (b) pieces.push(b); }
      pieces.push(...hood(c, { id: 'hood', liripipe: 0.6, style: S('#5e4027', '#4a3220', '#c9a13b', 0, 0.01) }));
      cover(band(c, { id: 'rope', y: waist, half: 0.014, thickness: 0.032, pleats: 9, puff: 0.004, style: S('#d8c39a', '#b59a68', '#8a7048', 8, 0.002), bones: ['pelvis', 'chest'] }));
      break;
    }
    case 'king': {
      const gown = torsoShell(c, { id: 'gown', neckDrop: 0.004, vNeck: 0, hemY: hemAbove, sleeve: 0.95, thickness: 0.014, loose: 0.01, style: S('#24418f', '#c9a13b', '#c9a13b', 2, 0.012) });
      cover(gown);
      const sk1 = skirt(c, gown, { id: 'gown-skirt', length: ankle, flare: 0.045, folds: 9, foldDepth: 0.005, arc: null, style: S('#24418f', '#c9a13b', '#c9a13b', 2, 0.016) });
      if (sk1) pieces.push(sk1);
      pieces.push(...cape(c, { id: 'mantle', length: -0.01, style: S('#982f29', '#f4efe6', '#c9a13b', 7, 0.02), mantle: S('#f4efe6', '#d8d0c0', '#1b1b1b', 6, 0.006) }));
      cover(band(c, { id: 'ruff', y: lerp(lm.chest[1], lm.neck[1], 0.88), half: 0.018, thickness: 0.03, pleats: 16, puff: 0.012, style: S('#f4efe6', '#d8d0c0', '#1b1b1b', 0, 0.002), bones: ['chest', 'neck'] }));
      break;
    }
    case 'bishop': {
      const alb = torsoShell(c, { id: 'alb', neckDrop: 0.004, vNeck: 0, hemY: hemAbove, sleeve: 0.92, thickness: 0.014, loose: 0.01, style: S('#f2ece0', '#c9a13b', '#b3261e', 0, 0.01) });
      cover(alb);
      const sk1 = skirt(c, alb, { id: 'alb-skirt', length: ankle - 0.005, flare: 0.035, folds: 12, foldDepth: 0.005, arc: null, style: S('#f2ece0', '#c9a13b', '#b3261e', 0, 0.016) });
      if (sk1) pieces.push(sk1);
      pieces.push(...cape(c, { id: 'cope', length: lerp(knee, ankle, 0.5), style: S('#c08a1e', '#9e2a1c', '#9e2a1c', 2, 0.016) }));
      break;
    }
    case 'wanderer': {
      const jer = torsoShell(c, { id: 'jerkin', neckDrop: 0.005, vNeck: 0.035, hemY: hemAbove, sleeve: 0.48, thickness: 0.013, loose: 0.006, neckTrim: false, style: S('#3f6b45', '#2b1d14', '#c9a13b', 0, 0.006) });
      cover(legShell(c, { id: 'hose', fromY: hip, toY: ankle + 0.025, thickness: 0.007, style: S('#7a5232', '#4a3220', '#c9a13b', 0, 0.008) }));
      cover(jer);
      const sk1 = skirt(c, jer, { id: 'jerkin-skirt', length: lerp(hip, knee, 0.45), flare: 0.02, folds: 7, foldDepth: 0.003, arc: null, style: S('#3f6b45', '#2b1d14', '#c9a13b', 0, 0.006) });
      if (sk1) pieces.push(sk1);
      pieces.push(...hood(c, { id: 'cowl', liripipe: 0.9, style: S('#2f5a37', '#2b1d14', '#c9a13b', 0, 0.012) }));
      cover(band(c, { id: 'belt', y: waist, half: 0.016, thickness: 0.028, pleats: 0, puff: 0, style: S('#5a3a1e', '#c9a13b', '#c9a13b', 0, 0.003), bones: ['pelvis', 'chest'] }));
      break;
    }
    case 'jester': {
      const mot = torsoShell(c, { id: 'motley', neckDrop: 0.004, vNeck: 0, hemY: hemAbove, sleeve: 0.95, thickness: 0.012, loose: 0.004, style: S('#b3261e', '#c9a13b', '#24418f', 3, 0.01) });
      cover(legShell(c, { id: 'hose', fromY: hip, toY: ankle + 0.02, thickness: 0.007, style: S('#24418f', '#b3261e', '#b3261e', 3, 0.008) }));
      cover(mot);
      const sk1 = skirt(c, mot, { id: 'motley-skirt', length: lerp(hip, knee, 0.4), flare: 0.03, folds: 8, foldDepth: 0.003, arc: null, dagged: 0.03, style: S('#b3261e', '#c9a13b', '#24418f', 3, 0.012) });
      if (sk1) pieces.push(sk1);
      cover(band(c, { id: 'ruff', y: lerp(lm.chest[1], lm.neck[1], 0.86), half: 0.02, thickness: 0.032, pleats: 12, puff: 0.016, style: S('#c9a13b', '#b3261e', '#24418f', 0, 0.002), bones: ['chest', 'neck'] }));
      break;
    }
    default: break;
  }
  const belt = pieces.find(p => p.id === 'belt');
  if (belt) pieces.push(buckle(belt));
  const hats: Record<string, Parameters<typeof hat>[1]> = {
    crusader: { id: 'helm', kind: 'helm', style: S('#9aa3ad', '#c9a13b', '#2b1d14', 4, 0.12) },
    king: { id: 'crown', kind: 'crown', style: S('#d9a92e', '#b3261e', '#2650a8', 4, 0.15) },
    bishop: { id: 'mitre', kind: 'mitre', style: S('#f2ece0', '#c9a13b', '#b3261e', 1, 0.1) },
    jester: { id: 'cap', kind: 'cap', style: S('#24418f', '#c9a13b', '#b3261e', 3, 0.12) },
  };
  if (hats[id]) pieces.push(fromFit(hat(body, hats[id]), center));
  if (collar && !pieces.some((p) => /ruff|hood|cowl|mantle/.test(p.id))) for (const f of catCollar(body)) pieces.push(fromFit(f, center));
  for (const piece of pieces) if (piece.style.pattern === 5) mailCoordinates(piece, body);
  const coveredBody = new Uint8Array(body.pos.length / 3);
  for (let v = 0; v < coveredBody.length; v++) coveredBody[v] = covered[w.map[v]];
  return { pieces, covered: coveredBody, cuffCut: cuffCut ? Float32Array.from(w.map, i => cuffCut![i]) : undefined };
}
